import React, { MouseEvent, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MarkdownDocTitle } from '../../../Markdown/styles';
import { createDocumentationLocationState } from '../../utils/documentationNavigationState';
import { resolveDocumentationLink } from '../../utils/resolveDocumentationLink';
import { scrollToDocumentationHeading } from '../../utils/scrollToDocumentationHeading';
import {
  createDocumentationHeadingIdTracker,
  parseDocumentationHtml,
} from './parser/parseDocumentationHtml';
import { DocumentationHtmlRoot } from './htmlDocStyles';
import { sanitizeDocumentationHtml } from './sanitizeDocumentationHtml';

interface HtmlDocProps {
  html: string;
  title?: string;
}

export function HtmlDoc({ html, title }: HtmlDocProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const sanitizedHtml = useMemo(() => sanitizeDocumentationHtml(html), [html]);
  const parsedHtml = useMemo(() => {
    const { assignHeadingId } = createDocumentationHeadingIdTracker();
    return parseDocumentationHtml(sanitizedHtml, {
      assignHeadingId,
    });
  }, [sanitizedHtml]);

  useEffect(() => {
    const headingId = location.hash.replace(/^#/, '');
    if (!sanitizedHtml || !headingId) {
      return;
    }

    scrollToDocumentationHeading(headingId);
  }, [location.hash, sanitizedHtml]);

  function handleDocumentationContentClick(
    event: MouseEvent<HTMLDivElement>,
  ) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) {
      return;
    }

    const anchorElement = eventTarget.closest('a');
    if (
      !anchorElement ||
      !event.currentTarget.contains(anchorElement) ||
      anchorElement.hasAttribute('download') ||
      (anchorElement.target && anchorElement.target !== '_self')
    ) {
      return;
    }

    const href = anchorElement.getAttribute('href');
    if (!href) {
      return;
    }

    const resolvedLink = resolveDocumentationLink({
      href,
    });

    if (resolvedLink.kind === 'internal') {
      event.preventDefault();
      navigate(resolvedLink.path, {
        state: createDocumentationLocationState(location),
      });
      return;
    }

    if (resolvedLink.kind === 'hash') {
      event.preventDefault();
      navigate(
        `${location.pathname}${location.search}#${resolvedLink.hash}`,
      );
      scrollToDocumentationHeading(resolvedLink.hash);
    }
  }

  return (
    <DocumentationHtmlRoot onClick={handleDocumentationContentClick}>
      {title ? <MarkdownDocTitle>{title}</MarkdownDocTitle> : null}
      {parsedHtml}
    </DocumentationHtmlRoot>
  );
}
